/**
 * Cash flow (TMMAPP-0145, split out of "Planned / Cashflow forecast"): where the balance is heading.
 * The scope picker (`?scope=`: combined, `a:{accountId}` or `e:{envelopeId}`) and the shared
 * forecast card on that scope's one projection (`PlannedCashflow`, TMMAPP-0112, MM-D-53). Opening
 * a series from it goes to Recurring's list with that series expanded.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { accountsApi, type Account } from '@/api/accounts';
import { plannedApi } from '@/api/planned';
import { ErrorState } from '@/components/ErrorState';
import { OwnedEnvelopesProvider } from '@/features/accounts/OwnedEnvelopesProvider';
import { isOwnerAccount } from '@/features/accounts/accountAccess';
import { useOwnedEnvelopes } from '@/features/accounts/useOwnedEnvelopes';
import type { ProjectionScope } from '@/features/cashflow/types';
import { useMe } from '@/session/meHooks';
import { stringParam, useUrlState } from '@/shared/useUrlState';
import { useNotify } from '@/shell/NotifyProvider';
import { V2_TABLE_HEADS_SX } from '@/components/v2TableHeads';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { PlannedCashflow } from './PlannedCashflow';
import { recurringSeriesLink } from './plannedRoutes';

const URL_SCHEMA = { scope: stringParam('combined') };

function CashFlowPageInner() {
  const { t } = useTranslation();
  const me = useMe();
  const notify = useNotify();
  const navigate = useNavigate();
  const [{ scope: scopeRaw }, setUrl] = useUrlState(URL_SCHEMA);
  const { envelopes: ownedEnvelopes } = useOwnedEnvelopes();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [seriesNames, setSeriesNames] = useState<Record<number, string>>({});
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null);
  const guest = me.tier === 'GUEST';

  const title = usePageTitle({ title: t('toolsNav.cashFlow') });

  const scopeEnvelope = scopeRaw.startsWith('e:')
    ? ownedEnvelopes.find((row) => row.id === Number(scopeRaw.slice(2)))
    : undefined;
  const scopeVirtualAccountId = scopeEnvelope?.virtualAccountId;
  const scope = useMemo<ProjectionScope>(
    () =>
      scopeRaw.startsWith('a:')
        ? { kind: 'account', id: Number(scopeRaw.slice(2)) }
        : scopeRaw.startsWith('e:')
          ? { kind: 'envelope', id: Number(scopeRaw.slice(2)) }
          : { kind: 'combined' },
    [scopeRaw],
  );

  // The scope picker's accounts and the register's series names.
  const load = useCallback(async () => {
    try {
      const [accts, series] = await Promise.all([accountsApi.list(), plannedApi.listSeries()]);
      setAccounts(accts.filter((a) => isOwnerAccount(a) || a.myPermissions.canEdit));
      setSeriesNames(Object.fromEntries(series.map((s) => [s.id, s.name])));
      setLoadFailure(null);
    } catch (error) {
      setLoadFailure({ error });
    }
  }, []);

  useEffect(() => {
    if (guest) return;
    queueMicrotask(() => {
      void load();
    });
  }, [guest, load]);

  const defaultCurrency = me.user.currency;
  // An account's own currency; an envelope's virtual account owner's; the combined view's operating one.
  const scopeAccount =
    scope.kind === 'account' ? accounts.find((a) => a.id === scope.id) : undefined;
  const currency =
    scope.kind === 'account'
      ? (scopeAccount?.currency ?? scopeAccount?.operatingCurrency ?? defaultCurrency)
      : scope.kind === 'envelope'
        ? (me.virtualAccounts.find((d) => d.id === scopeVirtualAccountId)?.currency ??
          defaultCurrency)
        : defaultCurrency;
  // tmm-api serves the table's remainder in the forecast's operating currency (TMMAPP-0175): an
  // account's own; an envelope's and the combined view's are already `currency`.
  const operatingCurrency =
    scope.kind === 'account' ? (scopeAccount?.operatingCurrency ?? defaultCurrency) : currency;

  const header = <PageHeader title={title} subtitle={t('cashflow.pageSubtitle')} />;

  if (guest) {
    return (
      <>
        {header}
        <Typography variant="body1">{t('cashflow.guestEmpty')}</Typography>
      </>
    );
  }

  return (
    // The v2 table heads (TMMAPP-0151 §H 24), scoped to this page.
    <Box sx={V2_TABLE_HEADS_SX} data-v2-table-heads="">
      {header}
      {loadFailure !== null && (
        <Box sx={{ mb: 2 }}>
          <ErrorState
            variant="section"
            error={loadFailure.error}
            what={t('errors.what.planned')}
            onRetry={() => void load()}
          />
        </Box>
      )}
      <PlannedCashflow
        scope={scope}
        currency={currency}
        operatingCurrency={operatingCurrency}
        labels={{
          envelopeNames: Object.fromEntries(
            ownedEnvelopes.map((envelope) => [envelope.id, envelope.name]),
          ),
          // An envelope on another virtual account than the scope's names its virtual account.
          envelopeVirtualAccounts: Object.fromEntries(
            ownedEnvelopes
              .filter((envelope) => envelope.virtualAccountId !== scopeVirtualAccountId)
              .map((envelope) => [envelope.id, envelope.virtualAccountName]),
          ),
          seriesNames,
        }}
        filter={
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="cashflow-scope">{t('cashflow.scopeLabel')}</InputLabel>
            <Select
              labelId="cashflow-scope"
              label={t('cashflow.scopeLabel')}
              value={scopeRaw}
              onChange={(event) => setUrl({ scope: event.target.value })}
            >
              <MenuItem value="combined">{t('cashflow.scopeCombined')}</MenuItem>
              {accounts
                .filter((a) => a.type === 'CASH')
                .map((a) => (
                  <MenuItem key={`a-${a.id}`} value={`a:${a.id}`}>
                    {a.name}
                  </MenuItem>
                ))}
              {ownedEnvelopes.map((envelope) => (
                <MenuItem key={`e-${envelope.id}`} value={`e:${envelope.id}`}>
                  {envelope.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
        onShowSeries={(seriesId) => {
          const link = recurringSeriesLink(seriesId);
          void navigate(link.to, link.options);
        }}
        onNotify={notify}
        // An edit or a split can make a series: its name is read again with the projection.
        onChanged={load}
      />
    </Box>
  );
}

export function CashFlowPage() {
  return (
    <OwnedEnvelopesProvider>
      <CashFlowPageInner />
    </OwnedEnvelopesProvider>
  );
}
